import React, { useState, useEffect } from 'react';
import BWAccordion from 'components/shared/BWAccordion';
import FormInput from 'components/shared/BWFormControl/FormInput';
import { useFormContext } from 'react-hook-form';
import { COUPON_PRODUCT_TYPE } from 'pages/Coupon/utils/constants';

import PropTypes from 'prop-types';
import styled from 'styled-components';
import PromotionsProductTable from 'pages/PromotionOffers/components/add/PromotionProductTable';
import PromotionProductModal from 'pages/PromotionOffers/components/add/PromotionProductModal';
import { PortalIcon } from 'utils/baochauTheme';

const Wrapper = styled.div`
  .bw_product_scope_grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 10px;
    margin-bottom: 12px;
  }

  .bw_product_scope_card {
    position: relative;
    display: grid;
    grid-template-columns: 38px minmax(0, 1fr) 20px;
    gap: 12px;
    align-items: center;
    min-height: 86px;
    padding: 14px 16px;
    border: 1px solid #d6dee9;
    border-radius: 12px;
    background: #fff;
    color: #344054;
    line-height: 20px;
    cursor: pointer;
    transition:
      border-color 0.18s ease,
      background-color 0.18s ease,
      box-shadow 0.18s ease,
      transform 0.18s ease;
  }

  .bw_product_scope_card:hover:not(.is-disabled) {
    border-color: #f2aaa5;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
    transform: translateY(-1px);
  }

  .bw_product_scope_card input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
  }

  .bw_product_scope_icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: #f2f4f7;
    color: #667085;
    font-size: 18px;
  }

  .bw_product_scope_content {
    min-width: 0;
  }

  .bw_product_scope_title {
    display: block;
    margin-bottom: 3px;
    color: #1d2939;
    font-size: 14px;
    font-weight: 700;
  }

  .bw_product_scope_description {
    display: block;
    color: #667085;
    font-size: 12.5px;
    font-weight: 400;
    line-height: 18px;
  }

  .bw_product_scope_indicator {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border: 2px solid #c7d0dd;
    border-radius: 50%;
    background: #fff;
  }

  .bw_product_scope_card.is-active {
    border-color: #ed3237;
    background: linear-gradient(180deg, #fffafa 0%, #fff5f5 100%);
    color: #1d2939;
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.08);
  }

  .bw_product_scope_card.is-active .bw_product_scope_icon {
    background: #fee4e2;
    color: #d92d32;
  }

  .bw_product_scope_card.is-active .bw_product_scope_indicator {
    border-color: #ed3237;
    box-shadow: inset 0 0 0 4px #fff;
    background: #ed3237;
  }

  .bw_product_selection_panel {
    padding: 14px;
    border: 1px solid #e4e7ec;
    border-radius: 12px;
    background: #fcfcfd;
  }

  .bw_product_selection_summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 8px;
    color: #475467;
    font-size: 13px;
  }

  .bw_product_selection_summary strong {
    color: #101828;
  }

  .bw_product_scope_card.is-disabled {
    cursor: not-allowed;
    opacity: 0.65;
  }

  @media (max-width: 1200px) {
    .bw_product_scope_grid {
      grid-template-columns: 1fr;
    }
  }
`;

const CouponProduct = ({ loading, title, disabled }) => {
  const methods = useFormContext();
  const { clearErrors, register, unregister, watch } = methods;
  const isAllProduct = watch('is_all_product');
  const productList = watch('product_list');
  const typeApplyProduct = watch('type_apply_product');
  const hasSelectedProducts = productList?.length > 0;
  const [modalCoupon, setModalCoupon] = useState(undefined);

  const handleChangeProductScope = (scope) => {
    if (scope === 'all') {
      methods.setValue('is_all_product', 1);
      methods.setValue('type_apply_product', null);
      return;
    }

    methods.setValue('is_all_product', 0);
    methods.setValue('type_apply_product', scope);
  };

  useEffect(() => {
    clearErrors('check_product');
    if (isAllProduct || hasSelectedProducts) {
      unregister('check_product', {});
    } else {
      register('check_product', { required: 'Vui lòng chọn ít nhất một sản phẩm' });
    }
  }, [clearErrors, hasSelectedProducts, isAllProduct, register, unregister]);

  return (
    <React.Fragment>
      <BWAccordion title={title}>
        <Wrapper className='bw_frm_box'>
          <div className='bw_col_12'>
            <div className='bw_product_scope_grid'>
              <FormInput hidden type='hidden' field='is_all_product' />
              <FormInput hidden type='hidden' field='type_apply_product' />
              <label
                className={`bw_product_scope_card ${Boolean(isAllProduct) ? 'is-active' : ''} ${
                  disabled ? 'is-disabled' : ''
                }`}>
                <input
                  disabled={disabled}
                  checked={Boolean(isAllProduct)}
                  type='radio'
                  name='bw_product_apply_scope'
                  onChange={() => handleChangeProductScope('all')}
                />
                <span className='bw_product_scope_icon'>
                  <PortalIcon icon='fi fi-rr-boxes' />
                </span>
                <span className='bw_product_scope_content'>
                  <span className='bw_product_scope_title'>Tất cả sản phẩm</span>
                  <span className='bw_product_scope_description'>Áp dụng coupon cho toàn bộ danh mục sản phẩm.</span>
                </span>
                <span className='bw_product_scope_indicator' />
              </label>
              <label
                className={`bw_product_scope_card ${
                  !Boolean(isAllProduct) && typeApplyProduct === COUPON_PRODUCT_TYPE.ANY ? 'is-active' : ''
                } ${disabled ? 'is-disabled' : ''}`}>
                <input
                  disabled={disabled}
                  checked={!Boolean(isAllProduct) && typeApplyProduct === COUPON_PRODUCT_TYPE.ANY}
                  type='radio'
                  name='bw_product_apply_scope'
                  onChange={() => handleChangeProductScope(COUPON_PRODUCT_TYPE.ANY)}
                />
                <span className='bw_product_scope_icon'>
                  <PortalIcon icon='fi fi-rr-shuffle' />
                </span>
                <span className='bw_product_scope_content'>
                  <span className='bw_product_scope_title'>Bất kỳ trong danh sách</span>
                  <span className='bw_product_scope_description'>Đơn hàng chỉ cần có một sản phẩm đã chọn.</span>
                </span>
                <span className='bw_product_scope_indicator' />
              </label>
              <label
                className={`bw_product_scope_card ${
                  !Boolean(isAllProduct) && typeApplyProduct === COUPON_PRODUCT_TYPE.APPOINT ? 'is-active' : ''
                } ${disabled ? 'is-disabled' : ''}`}>
                <input
                  disabled={disabled}
                  checked={!Boolean(isAllProduct) && typeApplyProduct === COUPON_PRODUCT_TYPE.APPOINT}
                  type='radio'
                  name='bw_product_apply_scope'
                  onChange={() => handleChangeProductScope(COUPON_PRODUCT_TYPE.APPOINT)}
                />
                <span className='bw_product_scope_icon'>
                  <PortalIcon icon='fi fi-rr-list-check' />
                </span>
                <span className='bw_product_scope_content'>
                  <span className='bw_product_scope_title'>Sản phẩm chỉ định</span>
                  <span className='bw_product_scope_description'>Chỉ áp dụng cho đúng sản phẩm đã chọn.</span>
                </span>
                <span className='bw_product_scope_indicator' />
              </label>
            </div>
            {!Boolean(methods.watch('is_all_product')) && (
              <div className='bw_product_selection_panel'>
                <div className='bw_product_selection_summary'>
                  <span>
                    Đã chọn <strong>{productList?.length || 0}</strong> sản phẩm
                  </span>
                  <span>Có thể tìm theo mã, tên hoặc IMEI/Serial</span>
                </div>
                <PromotionsProductTable
                  hiddenAction={{ detail: true }}
                  contentCreate='Chọn sản phẩm'
                  showCreateContent
                  noLoadData
                  handleOpenModalGift={() => setModalCoupon(true)}
                  fieldProduct='product_list'
                />
                <FormInput hidden={true} disabled={disabled} type='text' field='check_product' />
              </div>
            )}
          </div>
        </Wrapper>
      </BWAccordion>
      {modalCoupon && <PromotionProductModal fieldProduct='product_list' onClose={() => setModalCoupon(false)} />}
    </React.Fragment>
  );
};

CouponProduct.propTypes = {
  title: PropTypes.string,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default CouponProduct;
